/**
 * Rectangular Unit Composer
 *
 * Combines wall and floor primitives to create a complete rectangular unit.
 * Handles edge-based wall rendering with proper deduplication.
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import type { GeneratedUnit, UnitEdge } from '@/lib/templates/types'
import { renderUnitWalls, type WallRenderOptions } from '../primitives/WallRenderer'
import { renderFloor } from '../primitives/FloorRenderer'

/**
 * Unit render options
 */
export interface UnitRenderOptions {
  wallColor?: string
  floorColor?: string
  wallThickness?: number  // feet
  enableShadows?: boolean
  buildingName?: string  // For debug logging
}

/**
 * Determines if an edge should be rendered by this unit
 * Used for wall deduplication in multi-unit buildings
 *
 * Convention: For partition walls, the right-side unit renders the shared wall.
 * - Left edges of partition type: don't render (neighbor renders)
 * - Right edges of partition type: render (this unit owns it)
 * - All exterior edges: render
 * - Internal edges: never render (open bay mode)
 */
export function shouldRenderEdge(edge: UnitEdge): boolean {
  // Never render internal edges (open bay mode - no partition walls)
  if (edge.type === 'internal') {
    return false
  }

  if (edge.type === 'exterior') {
    return true
  }

  if (edge.type === 'partition') {
    // Left partition edges are shared with left neighbor - they render it
    // Right partition edges are owned by this unit - we render it
    return edge.side === 'right'
  }

  // Unknown edge types - don't render
  return false
}

/**
 * Render a rectangular unit from GeneratedUnit data
 */
export function renderUnit(
  unit: GeneratedUnit,
  options: UnitRenderOptions = {}
): THREE.Group {
  const {
    wallColor,
    floorColor,
    wallThickness = 0.5,
    enableShadows = true,
    buildingName = 'unknown',
  } = options

  // Debug: Log unit rendering details
  console.log(`🧱 [${buildingName}] Rendering unit "${unit.code}":`, {
    dimensions: { width: unit.interior_width, depth: unit.interior_depth, height: unit.interior_height },
    position: { offset_x: unit.offset_x, offset_y: unit.offset_y },
    rotation: unit.unit_rotation,
    edgeCount: unit.edges?.length || 0,
    edgeTypes: unit.edges?.map(e => `${e.side}:${e.type}`) || [],
  })

  const group = new THREE.Group()
  group.name = `unit-${unit.code}`
  group.userData = {
    unitCode: unit.code,
    unitType: unit.type,
    capacityStrategy: unit.capacity_strategy,
  }

  // Unit dimensions
  const dimensions = {
    width: unit.interior_width,
    depth: unit.interior_depth,
    height: unit.interior_height,
  }

  // Render floor
  const floor = renderFloor(
    { width: dimensions.width, depth: dimensions.depth },
    { color: floorColor, enableShadows }
  )
  group.add(floor)

  // Render walls from edges (if edges exist)
  if (unit.edges && unit.edges.length > 0) {
    // Filter edges to only render what this unit owns
    const edgesToRender = unit.edges.filter(shouldRenderEdge)

    console.log(`🧱 [${buildingName}] Unit "${unit.code}" edges to render:`, {
      total: unit.edges.length,
      filtered: edgesToRender.length,
      rendering: edgesToRender.map(e => `${e.side}:${e.type}`),
      skipped: unit.edges.filter(e => !shouldRenderEdge(e)).map(e => `${e.side}:${e.type}`),
    })

    const wallOptions: WallRenderOptions = {
      color: wallColor,
      enableShadows,
      wallThickness,
    }

    const walls = renderUnitWalls(edgesToRender, dimensions, wallOptions)
    group.add(walls)
  } else {
    console.warn(`🧱 [${buildingName}] Unit "${unit.code}" has NO EDGES - walls will not be rendered!`)
  }

  // Apply unit offset (position within building)
  // offset_x is the LEFT EDGE of the unit, but we render centered, so add half width
  // offset_y is already the correct Y position (0 for single-row buildings)
  const unitCenterX = unit.offset_x + unit.interior_width / 2

  group.position.set(
    unitCenterX * FEET_TO_METERS,
    0,
    -unit.offset_y * FEET_TO_METERS  // Negate: DB +Y = north, scene -Z = north (scene +Z = south)
  )

  // Apply unit rotation (relative to building)
  if (unit.unit_rotation !== 0) {
    group.rotation.y = unit.unit_rotation
  }

  return group
}

/**
 * Render multiple units for a multi-unit building
 */
export function renderUnits(
  units: GeneratedUnit[],
  options: UnitRenderOptions = {}
): THREE.Group {
  const group = new THREE.Group()
  group.name = 'units'

  for (const unit of units) {
    const unitGroup = renderUnit(unit, options)
    group.add(unitGroup)
  }

  return group
}
